import { z } from 'zod';
import { AdminApiError, adminFetch } from './api';
import type { AdminPagination, AdminUserDetails } from './types';

export const ADMIN_PAGE_SIZE = 25;
export const ADMIN_MAX_OFFSET = 1_000_000;
const paginationSchema = z.object({
  limit: z.number().int().min(1).max(100),
  offset: z.number().int().min(0).max(ADMIN_MAX_OFFSET),
  hasMore: z.boolean(),
});

// Only entirely absent pagination is a legacy response. Partial or mismatched
// pagination must not relabel old records as the requested page.
export async function fetchAdminPage<T extends { id: string }>(endpoint: string, key: string, offset: number, signal: AbortSignal, userId?: string): Promise<{ rows: T[]; pagination: AdminPagination | null }> {
  if (!Number.isInteger(offset) || offset < 0 || offset > ADMIN_MAX_OFFSET) throw new AdminApiError(400);
  const query = new URLSearchParams({ limit: String(ADMIN_PAGE_SIZE), offset: String(offset) });
  if (userId !== undefined) query.set('userId', userId);
  const payload = await adminFetch<Record<string, unknown>>(`/admin/${endpoint}?${query}`, { signal });
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload[key])) throw new AdminApiError(502);
  const rows = payload[key];
  if (!rows.every((row: unknown) => row !== null && typeof row === 'object' && 'id' in row && typeof row.id === 'string')) throw new AdminApiError(502);
  if (new Set(rows.map((row: { id: string }) => row.id)).size !== rows.length) throw new AdminApiError(502);
  if (['limit', 'offset', 'hasMore'].every((field) => !(field in payload))) {
    if (offset !== 0) throw new AdminApiError(502);
    return { rows: rows as T[], pagination: null };
  }
  const parsed = paginationSchema.safeParse(payload);
  if (!parsed.success || parsed.data.offset !== offset || parsed.data.limit !== ADMIN_PAGE_SIZE || rows.length > parsed.data.limit) throw new AdminApiError(502);
  return { rows: rows as T[], pagination: parsed.data };
}

const detailsSchema = z.object({
  user: z.object({
    id: z.string().min(1), email: z.string(), role: z.enum(['user', 'admin']), status: z.enum(['active', 'disabled', 'pending']),
    createdAt: z.string(), emailVerifiedAt: z.string().nullable(), updatedAt: z.string(),
  }),
  wallet: z.object({
    balanceUnits: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER), updatedAt: z.string(), currency: z.literal('CNY'), unit: z.literal('栀籽'),
  }).nullable(),
});

export async function fetchAdminUserDetails(id: string, signal: AbortSignal): Promise<AdminUserDetails> {
  const parsed = detailsSchema.safeParse(await adminFetch<unknown>(`/admin/users/${encodeURIComponent(id)}`, { signal }));
  if (!parsed.success || parsed.data.user.id !== id) throw new AdminApiError(502);
  return parsed.data;
}
